import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Search, CheckCircle2 } from "lucide-react";
import { POSTCODES, COMPANY } from "../data";
import { Reveal, Eyebrow } from "./Reveal";

export const Areas = ({ onQuote }) => {
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    const term = q.trim().toLowerCase();
    if (!term) return;
    const match = POSTCODES.some((p) => p.toLowerCase().includes(term) || term.includes(p.toLowerCase().split("-")[0]));
    // Essex covered broadly — treat any input as covered, highlight named towns
    setResult({ covered: true, exact: match, term: q.trim() });
  };

  return (
    <section id="areas" data-testid="postcode-checker" className="relative py-24 sm:py-32 bg-[#0c0c10]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow>Where we work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight">
              Covering all of <span className="flame-text">Essex</span>
            </h2>
            <p className="mt-4 max-w-md text-lg text-zinc-400">
              From Chelmsford to the coast — enter your town to confirm a local
              engineer can reach you.
            </p>

            <form onSubmit={check} className="mt-8 flex gap-2 max-w-md">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <input
                  data-testid="postcode-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e.g. Chelmsford or CM1"
                  className="w-full rounded-full border border-white/10 bg-[#121217] pl-11 pr-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#f5641e] focus:outline-none"
                />
              </div>
              <button
                type="submit"
                data-testid="postcode-check-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#f5641e] px-5 py-3.5 font-bold text-[#0a0a0c] hover:bg-[#ff7a38] transition-colors"
              >
                <Search className="h-4 w-4" /> Check
              </button>
            </form>

            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  data-testid="postcode-result"
                  className="mt-5 flex items-center gap-3 rounded-2xl border border-green-500/30 bg-green-500/10 px-5 py-4"
                >
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <div className="text-sm">
                    <span className="font-semibold text-white">Good news — we cover {result.term}.</span>{" "}
                    <button onClick={onQuote} className="text-[#f5641e] underline underline-offset-2">Request your quote</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2.5">
              {POSTCODES.map((p) => (
                <span
                  key={p}
                  className="rounded-full border border-white/10 bg-[#121217] px-4 py-2 text-sm text-zinc-300 hover:border-[#f5641e]/50 hover:text-white transition-colors"
                >
                  {p}
                </span>
              ))}
              <span className="rounded-full border border-[#f5641e]/40 bg-[#f5641e]/10 px-4 py-2 text-sm font-semibold text-[#f5641e]">
                + everywhere in between
              </span>
            </div>
            <p className="mt-6 font-mono-sx text-[11px] uppercase tracking-widest text-zinc-500">
              Not sure? Call {COMPANY.phone} — we'll tell you straight away.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
